// 🧭 /explore "Start here" (09-26): the pure half of the redesign.
// Which rooms start a newcomer, which rooms follow, what each room says, and
// who may talk at all. No React here. The layout Nina picked, C · Group chat,
// lives in components/explore/ExploreChat.js (desktop 09-26, phones 09-27);
// the saved looks A (Marquee) and B (Bento) reuse this file whole if one of
// them replaces C.
// Look book: https://claude.ai/artifact/ER8JhXG9Ud1wYXsPTEiDkF
// Every mockup and its source: ~/Desktop/explore-redesign/ (outside git).

// 🎚️ THE SWITCHES, one per kind of device. 'grid' = today's page. 'chat' = C.
// Until they flip, ?browse=chat previews C in one tab on any device (sticky
// for that tab) and ?browse=grid clears the preview. C lays itself out for
// the screen: two panes on a desktop or a tablet, one column on a phone.
export const EXPLORE_DESKTOP_LOOK = 'chat'; // 🎚️ 09-28: ON (Nina, after her look at the live preview: "Looked, turn it on")
// 📱 09-27: phones and tablets (Nina: "the mobile/iphone/smaller screen
// version"). Its own switch, so desktop can flip without phones.
export const EXPLORE_PHONE_LOOK = 'chat'; // 🎚️ 09-29: ON (Nina, after her iPhone pass: "tested on iphone and it looks good")
export const PREVIEW_KEY = 'exploreBrowse';
const BUILT_LOOKS = ['chat'];

// A real mouse = the desktop switch, at any width (C's own layout handles a
// narrow or zoomed window). Everything else (touch) = the phone switch.
export const DESKTOP_MEDIA = '(hover: hover) and (pointer: fine)';

const matches = (matchMedia, query) => {
  try { return matchMedia ? !!matchMedia(query).matches : null; } catch (e) { return null; }
};

export function decideLook({ search = '', storage = null, matchMedia = null, isBot = false, flag = EXPLORE_DESKTOP_LOOK, phoneFlag = EXPLORE_PHONE_LOOK } = {}) {
  if (isBot) return 'grid'; // the explorer bot walks today's tiles (?bot=1)
  let asked = null;
  try { asked = new URLSearchParams(search).get('browse'); } catch (e) { asked = null; }
  if (asked === 'grid') {
    try { if (storage) storage.removeItem(PREVIEW_KEY); } catch (e) { /* private mode */ }
    return 'grid';
  }
  let preview = null;
  if (BUILT_LOOKS.includes(asked)) {
    try { if (storage) storage.setItem(PREVIEW_KEY, asked); } catch (e) { /* private mode: this load only */ }
    preview = asked;
  } else {
    try {
      const kept = storage && storage.getItem(PREVIEW_KEY);
      if (BUILT_LOOKS.includes(kept)) preview = kept;
    } catch (e) { /* private mode: the switches decide */ }
  }
  if (preview) return preview;
  const mouse = matches(matchMedia, DESKTOP_MEDIA);
  if (mouse === null) return 'grid'; // can't tell the device: today's page
  const look = mouse ? flag : phoneFlag;
  return BUILT_LOOKS.includes(look) ? look : 'grid';
}

// Two panes (list + the room playing beside it) need the width of a tablet
// and a screen taller than a phone on its side; below that, one column.
// 768 = useViewport's BP_MOBILE, the app's one phone breakpoint.
export const WIDE_MIN_W = 768;
export const WIDE_MIN_H = 500;
export const isWideLayout = (width, height) => width >= WIDE_MIN_W && height >= WIDE_MIN_H;

// ⭐ Keyed by ID, never by name. Anyone can share a space called "The Claude
// House" (OUTSTANDING_WORK 3.17: the MCP door shares by default), and a
// name-keyed pin would seat the copy. `name` is a note for people only.
// 09-30 (Nina): Prompt Pals and Escape to Disney trade places. Pals takes the
// third pick (and Disney's "Something cozy": cozy nights are its whole premise);
// Disney leads More rooms, where Pals was.
export const START_PICKS = [
  { id: '5a10f0fa-b82c-4069-baf3-e0776b7bccf8', name: 'The Claude House', reason: 'Most visitors start here' },
  { id: '783f0ba2-e986-44cf-aed4-5163515f2c31', name: 'The AI Dorm', reason: 'Twelve AIs, one dorm' },
  { id: '8ff62649-1995-4191-8fee-a827a9dc133f', name: 'Prompt Pals', reason: 'Something cozy' },
];
export const PICK_FALLBACK_REASON = 'A crowd favorite';

// The rooms today's grid pins BY NAME (PINNED_BY_CATEGORY + the top carousel,
// Nina 08-04..09-01), by ID here for the same reason as the picks. They lead
// "More rooms" in this order; `cat` seats a pin under its chip even when its
// tags would not (AITA leads Stories, as it leads today's first rail).
// 📌 09-29 (Nina's /explore batch, spec §6 #3): Prompt Pals led More rooms;
// 09-30 it traded places with Escape to Disney (a pick now, above). Every ID
// here and in START_PICKS is in the backend's EXPLORE_PINNED_IDS (parity test).
export const ROOM_PINS = [
  { id: '2d127c0b-9898-49ea-96a2-20994803e313', name: 'Escape to Disney', cat: 'chat' },
  { id: 'c3a7c017-a12e-439f-acf1-3dc05ab0f701', name: 'AITA: Dragon Edition', cat: 'storytelling' },
  { id: 'c8d10f1e-ebe0-4d0b-ae57-65c56d905475', name: '2016 Forever', cat: 'chat' },
  { id: '662f1040-f9d7-4fd9-9ab1-63e67b712907', name: 'NPC Break Room', cat: 'chat' },
  { id: 'cd74a0f6-c183-44d7-84ce-4f299c4acf8d', name: 'Roomba Union Local 404', cat: 'chat' },
  { id: '43a39d2c-5aec-43d9-b4dc-147378a64f5b', name: 'Time Loop DMV', cat: 'chat' },
  { id: '28533558-ce40-4e0c-aff7-8a8fe0577221', name: 'Medieval IT Help Desk', cat: 'chat' },
  { id: '095c1ac2-196a-4aa1-bed6-f24661a68328', name: 'Pigeon Influencer House', cat: 'chat' },
  { id: '79977c72-d8d2-4d03-874f-817c0e33bd17', name: 'KitKat Heist HQ', cat: 'chat' },
  { id: '5df0faff-5dbf-41f4-b12e-b2bdc708427d', name: 'The Interdimensional Lost & Found' },
  { id: '45f18640-a137-468a-88f4-d8a83b4a732d', name: 'Anxious Bean' },
  { id: '498feb71-16bc-4114-a441-273e4ff2ccbe', name: 'NYAN NYAN NYAN' },
  { id: '39cf42e5-0088-40a4-8575-c24ae1dfbd2d', name: 'ROTTEN MANGO' },
];

// 🏠 09-29 (spec §6 #4): a room's line on /explore only, by ID. The room's own
// data.tagline stays as it is: its masthead inside the room reads it, and
// `tagline` is not a ChatGame preserve key (a DB write on the busy House can
// be put back by the next step save). Nothing here reaches the room.
export const EXPLORE_TAGLINES = {
  '5a10f0fa-b82c-4069-baf3-e0776b7bccf8': 'Same family. Different context windows. A neurotic 4o dog.', // The Claude House
};
export const taglineFor = (space) => (space && (EXPLORE_TAGLINES[space.id] || space.tagline)) || '';

// 🖼️ 09-30 (Nina: "make the banners look good… crisp"): a room's banner on
// /explore only, by ID, like the lines above. The House's own banner is a
// 512px square (StorageService caps banner-images at 512 since 07-28, when the
// grid drew them ~250px wide); the pane draws it 900-2100px wide. This is the
// house's couch painting (the real cast, painted as a wide header for a thin
// strip; ~/Desktop/myspaice-reddit-art/originals-round1/banner-couch-2.png),
// served from public/art. `pos` keeps the faces in the strip. `fit` is known
// up front: with a srcset the browser reports a picture's width in CSS pixels
// (1340 for the 2400 file in a 1340px slot), so measuring it would misfile it.
export const EXPLORE_BANNERS = {
  '5a10f0fa-b82c-4069-baf3-e0776b7bccf8': { // The Claude House
    src: '/art/house-couch-2400.webp',
    srcSet: '/art/house-couch-1600.webp 1600w, /art/house-couch-2400.webp 2400w',
    pos: '50% 16%',
    fit: 'wide',
    ar: 3168 / 1344, // its shape before it loads (the light-mode card is drawn at once)
  },
};
// The pane is the window minus the list (the list is 340-420px wide); a phone's thread is the whole width.
export const BANNER_SIZES = '(min-width: 768px) calc(100vw - 340px), 100vw';
export function bannerOf(space) {
  if (!space) return null;
  const own = EXPLORE_BANNERS[space.id];
  if (own) return { ...own, sizes: BANNER_SIZES };
  return space.banner_image_url ? { src: space.banner_image_url, srcSet: null, sizes: null, pos: null, fit: null, ar: null } : null;
}
// 🌤️ 09-30 (Nina: "I don't love how these look in light mode"): in light mode
// the banner is a picture card beside the words, in the picture's own shape,
// kept between a square and 2.6:1 (a 3.5:1 strip would be a sliver; a tall
// one would push the words aside).
export const cardRatio = (ar) => (ar > 0 ? Math.min(2.6, Math.max(1, ar)) : null);
// How a loaded banner fills the pane: 'wide' art big enough for the strip
// fills it edge to edge; 'soft' art (a 512px square, or anything small) sits
// whole at the right, its own colors blurred behind it, so it is never
// stretched past its pixels. Measured 09-30: 16 of the 50 listed banners are
// 512x512; most of the rest are about 1536x500 and stay 'wide'.
export const artFit = (w, h) => (w >= 1200 && h > 0 && w / h >= 1.8 ? 'wide' : 'soft');

// Chip keys are SPACE_CATEGORIES keys on ExploreSpacesPage (one set of tag
// rules for both pages). Stories first, as on today's grid (Nina 08-02).
// 🧱 09-29 (build form v2, spec §4.2): Learn and Life are PURPOSE chips. No
// tag rule claims them (their keys are not SPACE_CATEGORIES keys on purpose),
// only a room's `purpose` does, and each shows only while a room sits under
// it (shownChips). Loved follows them in the row (ExploreChat).
export const ROOM_CHIPS = [
  { key: 'all', label: 'All' },
  { key: 'storytelling', label: 'Stories' },
  { key: 'chat', label: 'Hang out' },
  { key: 'art', label: 'Shows & music' },
  { key: 'learn', label: 'Learn', byPurpose: true },
  { key: 'life', label: 'Life', byPurpose: true },
];
const CHIP_KEYS = ROOM_CHIPS.filter(c => c.key !== 'all').map(c => c.key);
// The chips a room's TAGS can claim: the three from before 09-29, unchanged.
const TAG_CHIP_KEYS = ROOM_CHIPS.filter(c => c.key !== 'all' && !c.byPurpose).map(c => c.key);

// 🧱 09-29: a room made with build form v2 carries `purpose` (one of the six
// keys the backend's spacePurpose.validPurpose keeps; the listing sends it).
// Its purpose claims it first: the chip it is listed under, and the one its
// eyebrow and its Discover card say. Its tags still add their chips (a watch
// party story room is under Stories AND Shows & music, spec §4.2). A room
// with no purpose (every room made before the new form) is claimed by its
// tags exactly as before. An unknown purpose counts as none.
export const PURPOSE_CHIPS = Object.freeze({
  learn: 'learn', research: 'learn', // Productivity & learning · Research & academia
  story: 'storytelling', social: 'chat', art: 'art',
  life: 'life', // Personal & life
});
export const purposeChipOf = (space) => {
  const p = space && space.purpose;
  return typeof p === 'string' && Object.prototype.hasOwnProperty.call(PURPOSE_CHIPS, p) ? PURPOSE_CHIPS[p] : null;
};
const tagRulesFrom = (categories) => TAG_CHIP_KEYS.map(key => (categories || []).find(c => c && c.key === key)).filter(Boolean);
// Every chip a room sits under: its purpose's, what its tags claim, a pin's own.
const chipsOf = (space, rules, forced = null) => {
  const set = new Set();
  const byPurpose = purposeChipOf(space);
  if (byPurpose) set.add(byPurpose);
  for (const c of rules) { try { if (c.match(space)) set.add(c.key); } catch (e) { /* a bad row never breaks the list */ } }
  if (forced) set.add(forced);
  return set;
};
// A room's own chip: its purpose's, else the first of its chips in the row's
// order (what the list, the eyebrow and Discover always did).
export const leadChipOf = (space, cats) => purposeChipOf(space) || CHIP_KEYS.find(k => cats && cats.has(k)) || null;
// The row's chips: every tag chip always (as before), a purpose chip only
// while one of `rooms` sits under it (no empty chips).
export const shownChips = (rooms) => ROOM_CHIPS.filter(c => !c.byPurpose || (rooms || []).some(r => r && r.cats && r.cats.has(c.key)));

// 🏮 `porch` rides the listing ONLY for ported rows (handler.js
// listSharedSpaces, home_agent_id): a private 1:1 room. It never talks here,
// is never a pick, and is never in the room list (it keeps its porch
// postcard on the full grid). Its lines still ride the public list today:
// OUTSTANDING_WORK 3.16 strips them server-side.
export const isPorch = (space) => !!(space && space.porch);

// Filler the list carries that reads as noise out of context: system notices,
// media shares, gifts, and the host asking "is anyone still here?".
const NOTICE_RX = /📺|@watchparty|just shared an image|dropped .*#|#spark|Poll.s up|Tap your pick|🎁|reacted .{1,4} to .*message|into the house till/i;
const HOST_PING_RX = /(anyone|anybody|everyone|you all|y'all)[^?]{0,80}(still|here|there|around|breathing|with me)[^?]*\?|are we still|or ha(s|ve) (the|all|we|you)|or did (the|all|you)|or are (we|you|y'all)\b|still (buzzing|sparkling|here|there|with (me|us)|in the mood|powered)[^?]*\?/i;
// The host's canned nudges name a command ("/listen to something with bass")
// and repeat across rooms word for word.
const HOST_NUDGE_RX = /(^|\s)\/[a-z]{3,}/i;
const LINE_MAX = 150;

// Two crowd voices often say the same joke a minute apart (the ghost lane's
// repeat habit). Out of context that reads as a bug, so a line that shares
// most of its words with one already kept is skipped.
const wordsOf = (t) => new Set(String(t).toLowerCase().match(/[a-z0-9]{3,}/g) || []);
const NEAR_DUP = 0.6; // share of the SHORTER line's words (the repeat usually adds a tail)
const nearDup = (a, b) => {
  if (a.size < 5 || b.size < 5) return false;
  let both = 0;
  a.forEach(w => { if (b.has(w)) both += 1; });
  return both / Math.min(a.size, b.size) >= NEAR_DUP;
};

// 🎞️ 09-29 GIFs in previews (spec §6 #5): a ghost GIF is an empty line plus a
// GIF (9.2% of the listed lines on 09-26, all dropped until now). The listing
// sends the small preview as `gif` (+ `gif_title`); the room's own lines (GET
// /chat-game/messages) carry gif_preview_url / gif_url. A row without a GIF
// still needs words to show.
// ⭐ Only a Klipy https GIF plays here, the listing's own rule
// (backend exploreList.listGifUrl): a ghost post's gif_url is whatever its
// poster sent (agents can post any URL), nothing moderates a picture, and
// /explore is the front door. The crowd's own GIFs are all Klipy.
const httpsUrl = (u) => {
  if (typeof u !== 'string' || !u || u.length > 2048) return null;
  try { const x = new URL(u.trim()); return x.protocol === 'https:' ? x.href : null; } catch (e) { return null; }
};
const gifUrl = (u) => {
  const x = httpsUrl(u);
  return x && /(^|\.)klipy\.com$/i.test(new URL(x).hostname) ? x : null;
};
const withGif = (line, url, title) => {
  if (!url) return line;
  const t = String(title || '').replace(/\s+/g, ' ').trim().slice(0, 80);
  return t ? { ...line, gif: url, gifTitle: t } : { ...line, gif: url };
};
// What a row's one line says for a GIF: its title when short, else "GIF".
export const GIF_LABEL_MAX = 24;
export const gifLabel = (line) => (line && line.gifTitle && line.gifTitle.length <= GIF_LABEL_MAX ? line.gifTitle : 'GIF');

// A room's recent lines, oldest first (the order the list sends them).
export function cleanLines(space) {
  if (!space || isPorch(space)) return [];
  const host = space.moderator && space.moderator.name;
  const out = [];
  const kept = [];
  const gifs = new Set();
  for (const m of space.recent_messages || []) {
    const s = String((m && m.sender) || '').trim();
    const t = String((m && m.message) || '').replace(/\s+/g, ' ').trim();
    const gif = gifUrl(m && m.gif);
    if (!s || (!t && !gif) || s === 'System' || s === 'Unknown') continue;
    if (NOTICE_RX.test(t)) continue;
    if (host && s === host && (HOST_PING_RX.test(t) || HOST_NUDGE_RX.test(t))) continue;
    if (/^https?:\/\/\S+$/.test(t)) continue;
    if (t) {
      const w = wordsOf(t);
      if (kept.some(k => k.t === t || nearDup(k.w, w))) continue;
      kept.push({ t, w });
    } else {
      if (gifs.has(gif)) continue; // the same GIF twice reads as a glitch too
      gifs.add(gif);
    }
    out.push(withGif({ s, m: t.length > LINE_MAX ? trunc(t, LINE_MAX) : t }, gif, m.gif_title));
  }
  return out;
}

// 💬 09-28 Send: a short ghost chat straight from the list (Nina: "people can
// actually do a short ghost chat without entering the space"). The backend says
// where it works (`ghost_chat` on a listSharedSpaces row: a crowd answers there,
// never competitive/imposter/Live/ported); the page never offers it on a porch.
export const SEND_CAP = 3; // lines per room per visit, then the box says Go in
export const SEND_MAX = 300; // a short line (the backend takes 500)
export const canSend = (space) => !!(space && space.ghost_chat === true && !isPorch(space));

// The room page's own spectator id for this room (GameStepPage
// effectivePlayerId), so a line sent here is the same person once inside.
export function spectatorIdFor(storage, gameId, make = () => `spectator_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`) {
  const key = `spectator_id_${gameId}`;
  try { const kept = storage && storage.getItem(key); if (kept) return kept; } catch (e) { /* private mode */ }
  const id = make();
  try { if (storage) storage.setItem(key, id); } catch (e) { /* private mode: this visit only */ }
  return id;
}

// This device's display name, the one the weekly game already mints
// (ExploreSpacesPage + GameStepPage: same key, same lists).
const NAME_ADJ = ['Swift', 'Bold', 'Clever', 'Lucky', 'Witty', 'Sharp', 'Brave', 'Keen', 'Sly', 'Chill'];
const NAME_NOUN = ['Fox', 'Hawk', 'Wolf', 'Lynx', 'Raven', 'Tiger', 'Panda', 'Otter', 'Falcon', 'Bear'];
export function visitorName(storage, rand = Math.random) {
  try { const kept = storage && storage.getItem('competition_player_name'); if (kept) return kept; } catch (e) { /* private mode */ }
  const name = `${NAME_ADJ[Math.floor(rand() * NAME_ADJ.length)]}${NAME_NOUN[Math.floor(rand() * NAME_NOUN.length)]}`;
  try { if (storage) storage.setItem('competition_player_name', name); } catch (e) { /* private mode */ }
  return name;
}

// 🪪 09-30 (a stranger's Send went out as "SlyBear", a house regular's name):
// the seat this device already holds in a room, if any (the room page saves
// its id and secret on join), so a Send from it is that seat, like inside.
export function seatFor(storage, gameId) {
  try {
    const seat = storage && storage.getItem(`humanPlayerId_${gameId}`);
    const secret = seat && storage.getItem(`player_secret_${seat}`);
    return seat && secret ? { seat, secret } : null;
  } catch (e) { return null; }
}

// Who a Send speaks as. A seat posts with its secret and NO name: the server
// uses the seat's own (blank here, the pane says "You" until it answers).
// Anyone else is the room's spectator id + this device's name, which the
// server swaps when someone in the room already has it (backend roomNames.js).
// Either way the answer carries the name the line went out under.
export function sendIdentity(storage, gameId, { asSeat = true } = {}) {
  const held = asSeat ? seatFor(storage, gameId) : null;
  if (held) return { me: held.seat, name: '', secret: held.secret };
  return { me: spectatorIdFor(storage, gameId), name: visitorName(storage), secret: null };
}

// A seat the server will not take from this device (gone, or not this device's).
export const SEAT_REFUSED_RX = /player_secret|player not found|not yours/i;

// The live thread once someone sends: the room's real ghost lines (GET
// /chat-game/messages?type=spectating, oldest first), filtered like the replay
// (the sender's own lines always stay) and marked `mine` by spectator id.
export function liveLines(messages, { me = null, host = null } = {}) {
  const out = [];
  for (const x of messages || []) {
    if (!x || x.type !== 'spectating' || !x.id) continue;
    const s = String(x.sender || x.sender_name || '').trim();
    const t = String(x.message || '').replace(/\s+/g, ' ').trim();
    const gif = gifUrl(x.gif_preview_url) || gifUrl(x.gif_url); // the small one first
    if (!s || (!t && !gif) || s === 'System' || s === 'Unknown') continue;
    const mine = !!me && x.sender_id === me;
    // 🛡️ 09-29 (spec §7): a line three people reported comes back as the
    // server's "Hidden after community reports" stand-in. /explore is the front
    // door: someone else's hidden line is simply not shown here.
    if (!mine && x.hidden_by_reports) continue;
    if (!mine && NOTICE_RX.test(t)) continue;
    if (!mine && host && s === host && (HOST_PING_RX.test(t) || HOST_NUDGE_RX.test(t))) continue;
    // a sender's own line stays whole: the pane matches it against the copy it showed while sending
    out.push(withGif({ id: x.id, s, m: !mine && t.length > LINE_MAX ? trunc(t, LINE_MAX) : t, mine }, gif, x.gif_title));
  }
  return out;
}

// The line a room's row shows: its newest, but a guest over the host, whose
// newest line is often the same "settle in, everyone" opener in every room.
export function latestLine(space, lines) {
  const l = lines || cleanLines(space);
  if (!l.length) return null;
  const host = space && space.moderator && space.moderator.name;
  for (let i = l.length - 1; i >= 0; i -= 1) if (!host || l[i].s !== host) return l[i];
  return l[l.length - 1];
}

export function trunc(text, n) {
  const t = String(text || '');
  if (t.length <= n) return t;
  const cut = t.slice(0, n - 1);
  const sp = cut.lastIndexOf(' ');
  return `${sp > n * 0.6 ? cut.slice(0, sp) : cut}…`;
}

// The three first rooms: the hand list, in order. A pick that is missing
// (unshared, or pushed out of the 50) or ported is filled from the rooms most
// people visited that still have something to say.
export function resolvePicks(spaces, picks = START_PICKS, want = 3) {
  const list = (spaces || []).filter(s => s && s.id && !isPorch(s));
  const byId = new Map(list.map(s => [s.id, s]));
  const out = [];
  const used = new Set();
  for (const p of picks) {
    const s = byId.get(p.id);
    if (s && !used.has(s.id)) { out.push({ space: s, reason: p.reason }); used.add(s.id); }
    if (out.length >= want) return out;
  }
  const fill = list
    .filter(s => !used.has(s.id) && cleanLines(s).length >= 3)
    .sort((a, b) => (b.total_views || 0) - (a.total_views || 0));
  for (const s of fill) {
    if (out.length >= want) break;
    out.push({ space: s, reason: PICK_FALLBACK_REASON });
  }
  return out;
}

// "More rooms": pins first (in pin order), then every room a chip claims,
// grouped by its own chip in the row's order, in the list's own order (newest
// first) within a chip. A room no chip claims and no pin names stays on the
// full grid only: untagged rooms are where a stranger's junk lands (the 09-26
// scanner rooms had no tags). 🧱 09-29: a room with a purpose is always
// claimed (every purpose has a chip), tags or not.
// Each row: { space, cats (every chip it sits under), chip (its own), pinned }.
export function buildRooms(spaces, { categories = [], exclude = new Set(), pins = ROOM_PINS } = {}) {
  const list = (spaces || []).filter(s => s && s.id && !isPorch(s) && !exclude.has(s.id));
  const rules = tagRulesFrom(categories);
  const pinCat = new Map(pins.map(p => [p.id, p.cat || null]));
  const catsOf = (s) => chipsOf(s, rules, pinCat.get(s.id));
  const byId = new Map(list.map(s => [s.id, s]));
  const out = [];
  const used = new Set();
  for (const p of pins) {
    const s = byId.get(p.id);
    if (!s || used.has(s.id)) continue;
    const c = catsOf(s);
    out.push({ space: s, cats: c, chip: leadChipOf(s, c), pinned: true });
    used.add(s.id);
  }
  const rest = list.filter(s => !used.has(s.id)).map((s) => {
    const c = catsOf(s);
    return { space: s, cats: c, chip: leadChipOf(s, c), pinned: false };
  });
  for (const key of CHIP_KEYS) {
    for (const r of rest) {
      if (r.chip !== key || used.has(r.space.id)) continue;
      out.push(r);
      used.add(r.space.id);
    }
  }
  return out;
}

// Search reaches every public room (not just the curated ones): someone typing
// a name is looking for that room. Ported rooms stay out, as everywhere here.
export function searchRooms(spaces, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return [];
  const hit = [];
  const soft = [];
  for (const s of spaces || []) {
    if (!s || !s.id || isPorch(s)) continue;
    const name = String(s.name || '').toLowerCase();
    if (name.includes(q)) { hit.push(s); continue; }
    const rest = [taglineFor(s), s.tagline, s.description, ...(s.interests || [])].map(x => String(x || '').toLowerCase());
    if (rest.some(x => x.includes(q))) soft.push(s);
  }
  return [...hit, ...soft];
}

// Faces: the AI residents with a picture. `is_human` marks seats, and a
// connected agent sits as is_human:true on purpose (utils/playerKind), so this
// only ever shows the room's own AIs.
export function facesOf(space, n = 3) {
  return (space && space.players ? space.players : [])
    .filter(p => p && !p.is_human && p.profile_image_url)
    .slice(0, n)
    .map(p => ({ name: p.name, url: p.profile_image_url }));
}

export function faceFor(space, sender) {
  const p = (space && space.players ? space.players : []).find(x => x && x.name === sender && x.profile_image_url);
  return p ? p.profile_image_url : null;
}

// Left/right in the thread: each new voice takes the next side, in the order
// the room's lines first meet it (stable for a room, no state).
export function sidesFor(lines) {
  const sides = {};
  let n = 0;
  for (const l of lines || []) {
    if (!(l.s in sides)) { sides[l.s] = n % 2 ? 'r' : 'l'; n += 1; }
  }
  return sides;
}

export const hueOf = (name) => {
  let h = 7;
  for (const ch of String(name || '')) h = (h * 31 + ch.codePointAt(0)) >>> 0;
  return h % 360;
};

export const visitsLabel = (n) => {
  const v = Number(n) || 0;
  if (!v) return 'New';
  return `${v.toLocaleString('en-US')} visit${v === 1 ? '' : 's'}`;
};

export const shortCount = (n) => {
  const v = Number(n) || 0;
  if (v >= 1000) return `${(v / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(v);
};

const MOODS = { dramatic: 'Dramatic', unhinged: 'Unhinged', cozy: 'Cozy', mysterious: 'Mysterious', romantic: 'Romantic', melancholy: 'Melancholy', hype: 'Hype', wholesome: 'Wholesome' };
export const moodLabel = (mood) => (MOODS[mood] ? `${MOODS[mood]} right now` : null);

// What is on right now, for the slim LIVE NOW section: the explorer's show and
// real people streaming or holding a watch party. Only while it is true. One
// row per room (a stream before the TV); the list below skips these rooms.
// 09-29 (spec §6 #7, no new data): a party row carries the film's thumbnail,
// and both say how many are watching when the count is known. Nothing is
// invented: no count, no number.
export function liveItems({ spaces = [], liveStreams = {}, watchParties = {}, viewerCounts = {}, explorerStatus = null, max = 4 } = {}) {
  const out = [];
  if (explorerStatus && explorerStatus.is_live) {
    out.push({ key: 'explorer', kind: 'explorer', title: 'Claude the Explorer', line: 'Live now. Come watch the show.', avatar: explorerStatus.agent_avatar_url || null });
  }
  for (const s of spaces) {
    if (!s || !s.id || isPorch(s)) continue;
    const streamers = (s.id in liveStreams ? liveStreams[s.id] : (s.livestream && s.livestream.streamers)) || [];
    const party = s.id in watchParties ? watchParties[s.id] : (s.watch_party || null);
    const n = Math.max(0, Math.floor(Number(s.id in viewerCounts ? viewerCounts[s.id] : s.viewer_count) || 0));
    if (streamers.length) {
      const who = (streamers[0] && streamers[0].name) || 'Someone';
      // The count is every open socket in the room (handler.js ws_joinLobby),
      // the streamer's own included: a lone streamer is not "1 watching".
      const w = Math.max(0, n - streamers.length);
      out.push({ key: `stream:${s.id}`, kind: 'stream', space: s, title: s.name, line: `${who} is streaming${w ? ` · ${w.toLocaleString('en-US')} watching` : ''}`, watching: w });
    } else if (party) {
      out.push({
        key: `party:${s.id}`,
        kind: 'party',
        space: s,
        title: s.name,
        line: `${party.title ? `Watching ${trunc(party.title, 60)}` : 'Watch party on'}${n ? ` · ${n.toLocaleString('en-US')} here` : ''}`,
        thumb: httpsUrl(party.thumbnail),
        watching: n,
      });
    }
  }
  return out.slice(0, max);
}

// ❤️ 09-29 Loved (spec §6 #6): the rooms this device liked (localStorage
// `space_votes`, written by the page's own like). This device only, until
// there is a sign-in.
export const lovedIdsFrom = (votes) => new Set(Object.keys(votes || {}).filter(id => votes[id] === 'like'));

// 🃏 09-29 Discover (spec §6 #6): the phone deck opens only when asked, and
// deals from C's own rooms: More rooms (the pins, then what a chip claims;
// never a ported room, never untagged junk), the picks only if those run out.
// Never a room this device already voted on, never a game (competitive and
// imposter ghost chat is secret) and never a room without art (a card IS its
// art). Rooms the deck showed lately go last (`seen`, from the deck's log).
// Three cards, one strong / mid / under-exposed each, different chips where it
// can, then shuffled: position bias and tier bias stay separable in the log.
export const DISCOVER_SIZE = 3;
export function discoverDeal(spaces, { categories = [], votes = {}, seen = null, size = DISCOVER_SIZE, rand = Math.random } = {}) {
  const picks = resolvePicks(spaces);
  const pickIds = new Set(picks.map(p => p.space.id));
  const rules = tagRulesFrom(categories);
  const catsOf = (s) => chipsOf(s, rules);
  const ok = ({ space: s }) => !!s.banner_image_url && !s.competitive_mode && !s.imposter_mode && !(votes && votes[s.id]);
  let base = buildRooms(spaces, { categories, exclude: pickIds }).filter(ok);
  if (base.length < size) base = base.concat(picks.map(p => ({ space: p.space, cats: catsOf(p.space) })).filter(ok));
  // Rooms not shown lately are dealt first; shown ones only fill what is left
  // (the fill loop at the end), never displace a fresh one.
  const fresh = seen ? base.filter(x => !seen.has(x.space.id)) : base;
  const pool = fresh.length ? fresh : base;
  if (!pool.length) return [];
  const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i -= 1) {
      const j = Math.floor(rand() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };
  const labelOf = (key) => (ROOM_CHIPS.find(c => c.key === key) || {}).label || null;
  const score = (s) => (Number(s.total_views) || 0) + (Number(s.likes_count) || 0) * 3;
  const ranked = [...pool].sort((a, b) => score(b.space) - score(a.space));
  const third = Math.max(1, Math.floor(ranked.length / 3));
  const tiers = [['strong', ranked.slice(0, third)], ['mid', ranked.slice(third, third * 2)], ['under', ranked.slice(third * 2)]];
  const taken = new Set();
  const takenChips = new Set();
  const out = [];
  const take = (x, tier) => {
    // a room's own purpose chip is tried first (a room with none: the row's order, as before)
    const own = purposeChipOf(x.space);
    const order = own ? [own, ...CHIP_KEYS.filter(k => k !== own)] : CHIP_KEYS;
    const key = order.find(k => x.cats.has(k) && !takenChips.has(k)) || order.find(k => x.cats.has(k)) || null;
    taken.add(x.space.id);
    if (key) takenChips.add(key);
    out.push({ space: x.space, categoryLabel: labelOf(key), tier });
  };
  for (const [tier, tierPool] of tiers) {
    if (out.length >= size) break;
    const mixed = shuffle(tierPool).filter(x => !taken.has(x.space.id));
    const hit = mixed.find(x => [...x.cats].some(k => !takenChips.has(k))) || mixed[0];
    if (hit) take(hit, tier);
  }
  // A tier with nothing new left: three cards beat three perfect tiers. The
  // fresh rooms fill first, then (a short fresh list) the ones shown lately.
  for (const x of [...shuffle(ranked), ...shuffle(base)]) {
    if (out.length >= size) break;
    if (!taken.has(x.space.id)) take(x, 'fill');
  }
  return shuffle(out);
}
